/**
 * Pasture Design System — Chip component
 *
 * A compact, read-only label for status, category, or metadata.
 *
 * - `primary`   — filled solid background, `--text-on-accent` text (white in light, dark in dark). Default fill is brand blue;
 *                 override via `--chip-fill-color` / `--chip-fill-color-hover`.
 * - `secondary` — transparent background, inset border + text in brand blue.
 *                 Override colours via CSS custom properties on the `style` prop:
 *                 `--chip-bg`, `--chip-bg-hover`, `--chip-border-color`,
 *                 `--chip-border-color-hover`, `--chip-text-color`,
 *                 `--chip-text-color-hover`, `--chip-shadow-hover`.
 *
 * Hover effects are opt-in via the `interactive` prop. A chip is always one target: the whole pill, or nothing.
 *
 * @example
 * <Chip variant="primary">Draft</Chip>
 * <Chip variant="secondary" interactive onClick={openFilter}>Filter</Chip>
 * <Chip variant="secondary" tooltip="What this status means">Closed</Chip>
 */

import Tooltip, { type Placement } from '@ds/components/feedback/Tooltip';
import Typography from '@ds/components/typography/Typography';
import clsx from 'clsx';
import React, { forwardRef, type ReactNode } from 'react';

import styles from './Chip.module.css';

export type ChipVariant = 'primary' | 'secondary';
export type ChipSize = 'sm' | 'md' | 'lg';

export interface ChipProps extends React.HTMLAttributes<HTMLSpanElement> {
    /**
     * Visual style of the chip.
     * - `primary`   — filled solid background, `--text-on-accent` text (white in light, dark in dark). Defaults to action-primary blue.
     *                 Override fill color via `--chip-fill-color` and `--chip-fill-color-hover`
     *                 in the `style` prop.
     * - `secondary` — transparent background, inset border + text in action-primary blue
     * @default 'primary'
     */
    variant?: ChipVariant;
    /**
     * Physical size.
     * - `sm` — compact (20px height)
     * - `md` — default; matches TAG typography scale (24px height)
     * - `lg` — spacious (32px height)
     *
     * @default 'md'
     */
    size?: ChipSize;
    /**
     * When true, the chip joins the tab order and darkens on pointer hover.
     * Primary darkens its background; secondary fills with a grey tint and darkens its border.
     * Set it for a chip with an `onClick`; a `tooltip` makes the chip interactive without it.
     *
     * @default false
     */
    interactive?: boolean;
    /**
     * Renders the chip in its inert, greyed-out visual (`--action-disabled` border,
     * `--text-disabled` text) and disables pointer interaction (hover, click, tooltip).
     * A consumer that sets its own `--chip-*` custom properties (e.g. `AccreditationChip`)
     * should override them again under `[data-disabled]` for its specific disabled colours —
     * this prop only supplies the generic fallback.
     *
     * @default false
     */
    disabled?: boolean;
    /** Icon rendered before the label. */
    startIcon?: ReactNode;
    /** Icon rendered after the label. */
    endIcon?: ReactNode;
    /**
     * Label content. Required: the label is what the chip means, and the icons are decorative, so a chip with
     * no label has nothing to say to assistive technology.
     */
    children: Exclude<ReactNode, null | undefined | boolean>;
    /**
     * Tooltip text shown on hover and on keyboard focus. A tooltip makes the chip interactive, so a keyboard user
     * can always reach the text. With no `onClick` the chip is a toggletip: it announces as a button, and a click,
     * tap, Enter or Space pins the tooltip open until Escape, a second click or a press outside.
     */
    tooltip?: string;
    /** Placement of the tooltip relative to the chip. */
    tooltipPlacement?: Placement;
}

const Chip = forwardRef<HTMLSpanElement, ChipProps>(
    (
        {
            variant = 'primary',
            size = 'md',
            interactive = false,
            disabled = false,
            startIcon = null,
            endIcon = null,
            children,
            tooltip,
            tooltipPlacement,
            className,
            ...rest
        },
        ref
    ) => {
        // An interactive chip is something the user can act on, so it must be reachable by keyboard: it joins the
        // tab order, and when it has an onClick it announces as a button and fires on Enter / Space like one. A
        // tooltip always makes the chip interactive: text that only hover can reach would shut out keyboard and
        // touch users, so it is not something a caller can opt into by leaving `interactive` off. With a tooltip
        // and no onClick the chip is a toggletip: still a button, because pressing it pins the tooltip open
        // (Tooltip's `toggle` owns the click and keys), which is also how a touch user reaches the text. A
        // read-only chip stays an inert span. Disabled chips leave the tab order, the same as a disabled <button>.
        const hasTooltip = tooltip != null && tooltip !== '';
        const isInteractive = interactive || hasTooltip;
        const isFocusable = isInteractive && !disabled;
        const actsAsButton = isFocusable && rest.onClick != null;
        const isToggletip = isFocusable && !actsAsButton && hasTooltip;
        const handleKeyDown = (e: React.KeyboardEvent<HTMLSpanElement>) => {
            rest.onKeyDown?.(e);
            if (!actsAsButton || e.defaultPrevented) return;
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                rest.onClick?.(e as unknown as React.MouseEvent<HTMLSpanElement>);
            }
        };

        const chip = (
            <span
                ref={ref}
                data-variant={variant}
                data-size={size}
                data-interactive={isInteractive ? '' : undefined}
                data-disabled={disabled || undefined}
                aria-disabled={disabled || undefined}
                className={clsx(styles.chip, className)}
                {...rest}
                role={rest.role ?? (actsAsButton || isToggletip ? 'button' : undefined)}
                tabIndex={rest.tabIndex ?? (isFocusable ? 0 : undefined)}
                onKeyDown={handleKeyDown}
            >
                {startIcon && <span className={styles.icon}>{startIcon}</span>}
                {children !== undefined && children !== null && (
                    <Typography
                        variant="tag"
                        component="span"
                        className={styles.label}
                    >
                        {children}
                    </Typography>
                )}
                {endIcon && <span className={styles.icon}>{endIcon}</span>}
            </span>
        );

        if (tooltip) {
            return (
                <Tooltip
                    text={tooltip}
                    placement={tooltipPlacement}
                    toggle={isToggletip}
                >
                    {chip}
                </Tooltip>
            );
        }

        return chip;
    }
);

Chip.displayName = 'Chip';

export { Chip };
export default Chip;
